import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

function CustomerList({ customers }) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState(customers);
  const [currentPage, setCurrentPage] = useState(1);
  const [customersPerPage, setCustomersPerPage] = useState(25);

  const handleSelectCustomer = (customer) => {
    navigate(`/customer-details/${customer._id}`);
  };

  useEffect(() => {
    // Filtrerer kunder basert på søkefelt
    if (searchQuery.length >= 2) {
      const filtered = customers.filter((customer) =>
        (customer.firstName || "").toLowerCase().includes(searchQuery) ||
        (customer.lastName || "").toLowerCase().includes(searchQuery) ||
        (customer.phoneNumber || "").includes(searchQuery) ||
        (customer.email || "").toLowerCase().includes(searchQuery)
      );
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers(customers); // Viser alle kunder hvis søkefeltet er tomt eller under 2 tegn
    }
    setCurrentPage(1); // Tilbakestiller til første side ved ny søking
  }, [searchQuery, customers]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  // Beregn totale sider
  const totalCustomers = filteredCustomers.length;
  const totalPages = Math.ceil(totalCustomers / customersPerPage);

  // Håndter visning av kunder per side
  const handleCustomersPerPageChange = (e) => {
    setCustomersPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1); // Tilbakestiller til første side når antallet endres
  };

  // Finn start og sluttindekser for gjeldende side
  const indexOfLastCustomer = currentPage * customersPerPage;
  const indexOfFirstCustomer = indexOfLastCustomer - customersPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstCustomer, indexOfLastCustomer);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  return (
    <div className="max-w-5xl mx-auto py-8 bg-white shadow-lg rounded-lg p-6 mb-4">
      <h3 className="text-2xl font-semibold mb-4 text-gray-800">Kunder</h3>

      {/* Søkefelt */}
      <input
        type="text"
        placeholder="Søk etter fornavn, etternavn, telefonnummer eller e-post"
        value={searchQuery}
        onChange={handleSearch}
        className="mb-4 p-2 border border-gray-300 rounded-md w-full"
      />



      {/* Kundeliste tabell */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-300 rounded-lg">
          <thead>
            <tr className="bg-gray-100">
              <th className="px-6 py-3 border-b border-gray-200 text-left text-sm font-semibold text-gray-600">
                <span className="block md:hidden">Navn</span>
                <span className="hidden md:block">Fornavn</span>
              </th>
              <th className="px-6 py-3 border-b border-gray-200 text-left text-sm font-semibold text-gray-600 hidden md:table-cell">Etternavn</th>
              <th className="px-6 py-3 border-b border-gray-200 text-left text-sm font-semibold text-gray-600">Telefon</th>
              <th className="px-6 py-3 border-b border-gray-200 text-left text-sm font-semibold text-gray-600 hidden md:table-cell">E-post</th>
              <th className="px-6 py-3 border-b border-gray-200 text-left text-sm font-semibold text-gray-600 hidden md:table-cell">Handlinger</th>
            </tr>
          </thead>
          <tbody>
            {currentCustomers.map((customer) => (
              <tr
                key={customer._id}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => handleSelectCustomer(customer)}
              >
                <td className="px-6 py-4 border-b border-gray-200 text-sm text-gray-700">
                  <span className="block md:hidden">
                    {customer.firstName} {customer.lastName}
                  </span>
                  <span className="hidden md:block">
                    {customer.firstName}
                  </span>
                </td>
                <td className="px-6 py-4 border-b border-gray-200 text-sm text-gray-700 hidden md:table-cell">
                  {customer.lastName}
                </td>
                <td className="px-6 py-4 border-b border-gray-200 text-sm text-gray-700">
                  {customer.phoneNumber}
                </td>
                <td className="px-6 py-4 border-b border-gray-200 text-sm text-gray-700 hidden md:table-cell">
                  {customer.email}
                </td>
                <td className="px-6 py-4 border-b border-gray-200 text-sm hidden md:table-cell">
                  <Link
                    to={`/customer-details/${customer._id}`}
                    className="text-blue-500 hover:underline"
                  >
                    Se detaljer
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Paginering */}
      <div className="flex justify-between items-center mt-4">
        {/* Valg for antall kunder per side */}
        <div className="mb-4">
          <label className="text-sm font-medium text-gray-700 mr-2">Vis:</label>
          <select value={customersPerPage} onChange={handleCustomersPerPageChange} className="p-2 border border-gray-300 rounded-md">
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <div>
          Viser {indexOfFirstCustomer + 1}-{Math.min(indexOfLastCustomer, totalCustomers)} av {totalCustomers} kunder
        </div>
        <div className="flex space-x-2">
          {[...Array(totalPages)].map((_, index) => (
            <button
              key={index}
              onClick={() => handlePageChange(index + 1)}
              className={`px-4 py-2 rounded-md ${currentPage === index + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-800'
                }`}
            >
              {index + 1}
            </button>

          ))}
        </div>
      </div>
    </div>
  );
}

export default CustomerList;
